import { API_BASE_URL } from '@types';
import type {
  PatientBaseProfile,
  PatientHubListItem,
  PatientMeasurementRecord,
  PatientRecord,
} from '../types';
import {
  buildPatientBodyMeasurementPayload,
  normalizePatientMeasurementRecord,
  normalizePatientRecord,
} from '../utils/normalize';
import { isFullBodyMeasurementRecord } from '../utils/measurements';
import { MEASUREMENT_INCOMPLETE_MESSAGE } from '../utils/measurementValidation';
import {
  isRegisteredBiologicalSex,
  isRegisteredDateOfBirth,
  isRegisteredHeight,
} from '../utils/profile';

type PatientBaseProfilePatch = Partial<PatientBaseProfile>;

const patchPatientBaseProfile = async (
  patientId: number,
  profile: PatientBaseProfilePatch
): Promise<PatientRecord | null> => {
  const body: Record<string, unknown> = {};

  if ('dateOfBirth' in profile) {
    body.dateOfBirth =
      typeof profile.dateOfBirth === 'string' &&
      profile.dateOfBirth.trim() !== ''
        ? profile.dateOfBirth.trim()
        : null;
  }

  if ('biologicalSex' in profile) {
    body.biologicalSex = profile.biologicalSex;
  }

  if ('heightCm' in profile) {
    body.heightCm = profile.heightCm;
  }

  if (Object.keys(body).length === 0) {
    return null;
  }

  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/patients/${patientId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    }
  );

  const normalized = normalizePatientRecord(unwrapData<unknown>(payload));

  return normalized;
};

const isPatientProfilePatchUnavailable = (error: unknown): boolean => {
  const status = (error as Error & { status?: number }).status;
  // Staging currently remaps Middy "Route does not exist" (404) to HTTP 500.
  return status === 404 || status === 405 || status === 500;
};

const persistProfileFieldsFromMeasurement = async (
  patientId: number,
  values: PatientMeasurementRecord
): Promise<'persisted' | 'skipped' | 'unavailable'> => {
  const profilePatch: PatientBaseProfilePatch = {};

  if (isRegisteredBiologicalSex(values.biologicalSex)) {
    profilePatch.biologicalSex = values.biologicalSex;
  }

  if (isRegisteredDateOfBirth(values.dateOfBirth)) {
    profilePatch.dateOfBirth = values.dateOfBirth.trim();
  }

  if (isRegisteredHeight(values.heightCm)) {
    profilePatch.heightCm = values.heightCm;
  }

  if (Object.keys(profilePatch).length === 0) {
    return 'skipped';
  }

  try {
    await patchPatientBaseProfile(patientId, profilePatch);
    return 'persisted';
  } catch (error) {
    if (!isPatientProfilePatchUnavailable(error)) {
      throw error;
    }

    console.warn(
      '[patientsService] patient profile PATCH unavailable; skipping profile persist',
      {
        patientId,
        status: (error as Error & { status?: number }).status,
      }
    );
    return 'unavailable';
  }
};

const unwrapData = <T>(payload: unknown): T => {
  if (
    payload != null &&
    typeof payload === 'object' &&
    'data' in (payload as Record<string, unknown>)
  ) {
    return (payload as { data: T }).data;
  }

  return payload as T;
};

const requestJson = async <T>(input: RequestInfo, init?: RequestInit) => {
  const requestUrl = typeof input === 'string' ? input : input.toString();
  const requestMethod = init?.method ?? 'GET';

  const response = await fetch(input, {
    credentials: 'include',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    const responseText = await response.text();
    const error = new Error(
      `HTTP ${response.status}: ${response.statusText}${responseText ? `\n${responseText}` : ''}`
    );
    (error as Error & { status?: number }).status = response.status;
    console.error('[patientsService] request failed', {
      url: requestUrl,
      method: requestMethod,
      status: response.status,
      statusText: response.statusText,
      requestBody:
        typeof init?.body === 'string'
          ? init.body
          : init?.body != null
            ? '[non-string body]'
            : null,
      responseText,
    });
    throw error;
  }

  if (response.status === 204) {
    return null as T;
  }

  return (await response.json()) as T;
};

export async function fetchPatientById(
  patientId: number
): Promise<PatientRecord> {
  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/patients/${patientId}`
  );
  const normalized = normalizePatientRecord(unwrapData<unknown>(payload));

  if (normalized == null) {
    throw new Error('Invalid patient payload');
  }

  return normalized;
}

export async function fetchPatientsList(): Promise<PatientHubListItem[]> {
  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/getPatientsAndDevices`
  );

  const patients =
    unwrapData<{ patients?: Array<{ value?: string; label?: string }> }>(
      payload
    )?.patients ?? [];

  const ids = patients
    .map(patient => Number(patient.value ?? 0))
    .filter(id => Number.isFinite(id) && id > 0);

  const details = await Promise.allSettled(
    ids.map((id, index) =>
      fetchPatientById(id).catch(() => {
        const fallback = normalizePatientRecord(
          { id, code: patients[index]?.label ?? undefined },
          patients[index]?.label
        );

        if (fallback == null) {
          throw new Error('Invalid patient fallback payload');
        }

        return fallback;
      })
    )
  );

  const records = details
    .filter(
      (entry): entry is PromiseFulfilledResult<PatientRecord> =>
        entry.status === 'fulfilled'
    )
    .map(entry => entry.value)
    .sort((left, right) => left.code.localeCompare(right.code, 'it'));

  const measurementsResults = await Promise.all(
    records.map(patient =>
      fetchPatientMeasurementsHistory(patient.id).catch(
        () => [] as PatientMeasurementRecord[]
      )
    )
  );

  return records.map((patient, index) => ({
    ...patient,
    hasBodyMeasurements: measurementsResults[index]?.length > 0,
  }));
}

export async function updatePatientDateOfBirth(
  patientId: number,
  dateOfBirth: string | null
): Promise<PatientRecord> {
  const patched = await patchPatientBaseProfile(patientId, { dateOfBirth });

  if (patched != null) {
    return patched;
  }

  return fetchPatientById(patientId);
}

export async function updatePatientBaseProfile(
  patientId: number,
  profile: PatientBaseProfile
): Promise<PatientRecord> {
  const hasDateOfBirth =
    typeof profile.dateOfBirth === 'string' &&
    profile.dateOfBirth.trim() !== '';
  const hasBiologicalSex =
    profile.biologicalSex === 'male' || profile.biologicalSex === 'female';
  const hasHeight =
    typeof profile.heightCm === 'number' &&
    Number.isFinite(profile.heightCm) &&
    profile.heightCm > 0;

  if (!hasDateOfBirth && !hasBiologicalSex && !hasHeight) {
    throw new Error(
      'Inserisci almeno sesso biologico, altezza o anno di nascita per salvare il profilo base.'
    );
  }

  const patched = await patchPatientBaseProfile(patientId, {
    dateOfBirth: profile.dateOfBirth,
    biologicalSex: profile.biologicalSex,
    heightCm: profile.heightCm,
  });

  const result = patched ?? (await fetchPatientById(patientId));

  return result;
}

export async function deletePatientById(patientId: number): Promise<void> {
  await requestJson(`${API_BASE_URL}/services/patients/${patientId}`, {
    method: 'DELETE',
  });
}

export async function fetchPatientMeasurementsHistory(
  patientId: number
): Promise<PatientMeasurementRecord[]> {
  try {
    const payload = await requestJson<unknown>(
      `${API_BASE_URL}/services/patients/${patientId}/body-measurements`
    );
    const data = unwrapData<unknown>(payload);
    const rawItems = Array.isArray(data)
      ? data
      : data != null &&
          typeof data === 'object' &&
          Array.isArray((data as Record<string, unknown>).items)
        ? ((data as Record<string, unknown>).items as unknown[])
        : [];

    return rawItems
      .map(item => normalizePatientMeasurementRecord(item, patientId))
      .filter((item): item is PatientMeasurementRecord => item != null)
      .filter(isFullBodyMeasurementRecord)
      .sort((left, right) => {
        const leftDate = new Date(
          left.createdAt ?? left.updatedAt ?? 0
        ).getTime();
        const rightDate = new Date(
          right.createdAt ?? right.updatedAt ?? 0
        ).getTime();
        return rightDate - leftDate;
      });
  } catch (error) {
    if ((error as Error & { status?: number }).status === 404) {
      return [];
    }

    throw error;
  }
}

export async function deletePatientMeasurement(
  patientId: number,
  measurementId: number
): Promise<void> {
  await requestJson(
    `${API_BASE_URL}/services/patients/${patientId}/body-measurements/${measurementId}`,
    {
      method: 'DELETE',
    }
  );
}

export async function createPatientMeasurement(
  patientId: number,
  values: PatientMeasurementRecord
): Promise<PatientMeasurementRecord> {
  const body = buildPatientBodyMeasurementPayload(values);

  if (body == null) {
    throw new Error(MEASUREMENT_INCOMPLETE_MESSAGE);
  }

  await persistProfileFieldsFromMeasurement(patientId, values);

  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/patients/${patientId}/body-measurements`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );

  const normalized = normalizePatientMeasurementRecord(
    unwrapData<unknown>(payload),
    patientId
  );

  if (normalized == null) {
    throw new Error('Invalid measurement payload');
  }

  // Keep anagrafica from the form even if API response omits them
  // (e.g. before date_of_birth column exists / older deployments).
  return {
    ...normalized,
    biologicalSex: values.biologicalSex ?? normalized.biologicalSex ?? null,
    dateOfBirth: values.dateOfBirth ?? normalized.dateOfBirth ?? null,
    heightCm: values.heightCm ?? normalized.heightCm ?? null,
  };
}
